import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import _ from 'lodash';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Divider, Paper, Stack, Typography } from '@mui/material';
import { Form, Formik } from 'formik';

import useNotification from '../../../../../../core/hooks/use_notification';
import { getAllPodsState } from '../../../../../../features/pod/get_all_pods';
import { getProductBatchesState } from '../../../../../../features/inventory/get_product_batches';
import { Button, SubmitButton } from '../../../../../common/button';
import { SelectFieldMultipleFormikWithMore } from '../../../../../common/select_field';
import { AutocompleteMultipleFormik } from '../../../../../common/autocomplete';
import { deleteProductExpirationPromotionsAction, deleteProductExpirationPromotionsState } from '../../../../../../features/price/delete_product_expiration_promotions';
import { deleteProductExpirationPromotionsNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';
import { isEmptyArray } from '../../../../../../core/utils/misc_utils';

const DeletePromotionsFormDialog = ({ productId, refreshList }) => {
    const { showNotification } = useNotification();
    const dispatch = useDispatch();

    const getAllPods = useSelector(getAllPodsState);
    const getProductBatches = useSelector(getProductBatchesState);
    const deleteProductExpirationPromotions = useSelector(deleteProductExpirationPromotionsState);

    const [open, setOpen] = useState(false);

    const mapProductBatchOptions = useCallback((data) => {
        return data?.map(productBatch => ({
            key: productBatch?.id + Math.random(),
            value: productBatch?.productBatchId,
            label: `${ productBatch?.productBatchId }, nr magazynowy: ${ productBatch?.warehouseBatchNumber }`,
            expiryDate: productBatch?.expiryDate
        }));
    }, []);

    const productBatchOptions = [...mapProductBatchOptions(getProductBatches?.data)];

    const initialValues = {
        podIds: [],
        productBatches: []
    };

    const schema = Yup.object().shape({
        podIds: Yup.array().when('productBatches', {
            is: (productBatches) => isEmptyArray(productBatches),
            then: Yup.array().min(1, 'Proszę wybrać punkt sprzedaży lub partię produktu'),
            otherwise: Yup.array().notRequired()
        }),
        productBatches: Yup.array().when('podIds', {
            is: (podIds) => isEmptyArray(podIds),
            then: Yup.array().min(1, 'Proszę wybrać partię produktu lub punkt sprzedaży'),
            otherwise: Yup.array().notRequired()
        })
    }, ['podIds', 'productBatches']);

    const onClose = () => setOpen(false);

    const onSubmit = (values) => {
        const form = {
            ..._.omit(values, ['productBatches']),
            productBatchIds: values.productBatches.map(productBatch => productBatch.value)
        };

        dispatch(deleteProductExpirationPromotionsAction({ productId, form }))
            .then(response => {
                showNotification(deleteProductExpirationPromotionsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshList && refreshList();
                    onClose();
                }
            });
    };

    return (
        <>
            <Button color="error" variant="outlined" onClick={ () => setOpen(true) }>
                Usuń promocje masowo
            </Button>

            <Dialog open={ open } onClose={ onClose } fullWidth={ true } maxWidth="md">
                <Formik
                    initialValues={ initialValues }
                    validationSchema={ schema }
                    validateOnChange
                    validateOnBlur={ false }
                    onSubmit={ onSubmit }>
                    { () => (
                        <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column' } }>
                            <DialogTitle>
                                <Typography variant="h6" fontWeight="bold">
                                    Usuń promocje według punktów i partii
                                </Typography>
                            </DialogTitle>
                            <Divider/>

                            <DialogContent>
                                <Stack spacing={ 4 } sx={ { py: 1 } }>
                                    <Paper variant="outlined" sx={ { p: 2, backgroundColor: 'slate.50' } }>
                                        <Typography variant="subtitle2"
                                                    sx={ { fontWeight: 600, mb: 1 } }>
                                            Zasady usuwania:
                                        </Typography>
                                        <Box component="ol"
                                             sx={ { m: 0, pl: 2, typography: 'body2', color: 'text.secondary' } }>
                                            <li><b>Tylko pody:</b> Usunięte zostaną wszystkie promocje produktu w
                                                podanych podach.
                                            </li>
                                            <li><b>Tylko numery partii:</b> Usunięte zostaną wszystkie promocje produktu
                                                o podanych numerach partii.
                                            </li>
                                            <li><b>Pody oraz partie:</b> Wykona się usuwanie z punktu 1, a następnie z
                                                punktu 2.
                                            </li>
                                        </Box>
                                    </Paper>

                                    <Box>
                                        <SelectFieldMultipleFormikWithMore
                                            name="podIds"
                                            label="Wybierz pody - wiele"
                                            defaultOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId != null).map(pod => ({
                                                value: pod?.id,
                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + pod?.device?.serviceId
                                            })) }
                                            extraOptions={ getAllPods?.data?.filter(pod => pod?.device?.serviceId == null).map(pod => ({
                                                value: pod?.id,
                                                label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - brak urządzenia'
                                            })) }
                                        />
                                    </Box>

                                    <Box>
                                        <AutocompleteMultipleFormik
                                            options={ productBatchOptions }
                                            name="productBatches"
                                            label="Wybierz partie produktu - wiele"
                                            groupBy={ option => option.expiryDate }
                                        />
                                    </Box>
                                </Stack>
                            </DialogContent>

                            <Divider/>
                            <DialogActions sx={ { px: 3, py: 2, gap: 2 } }>
                                <Button variant="outlined" onClick={ onClose }>Anuluj</Button>
                                <SubmitButton isLoading={ deleteProductExpirationPromotions?.loading }>Usuń
                                    wybrane</SubmitButton>
                            </DialogActions>
                        </Box>
                    ) }
                </Formik>
            </Dialog>
        </>
    );
};

export default DeletePromotionsFormDialog;